import { Container, Heading, Link, Maps, Socials, useData } from '@/components';

import { FC } from 'react';
import { ILocationInfo, ScheduleWorkingHours } from '@/types';
import classNames from 'classnames';

import style from './locationinfo.module.scss';
import {
  formatEnUSPhoneNumber,
  getActiveWorkingHours,
  getCSSpropety,
  htmlSerializerWithProps,
  transformMilitaryTime,
} from '@/utils';
import { PrismicRichText } from '@prismicio/react';
import { asText, ColorField } from '@prismicio/client';
import { startCase } from 'lodash';
import { getMapStyle } from '../Maps';

const LocationInfo: FC<ILocationInfoProps> = ({ info, styleProps }) => {
  const socials = info.socials;
  const storeName = info.short_name;

  const { treezStores, store } = useData();

  const adminWorkingHours = store?.pickup_schedules?.filter(
    s => s?.stores_provider_id === storeName,
  )?.[0]?.working_hours;

  const configWorkingHours = treezStores?.find(
    store => store?.url_slug === info.storeUrlSlug,
  )?.working_hours;

  const networks = socials?.reduce((networks, network) => {
    if (network.social_link.link_type === 'Any') {
      return networks;
    }
    return {
      ...networks,
      [network?.social_id || '']: {
        url: (network?.social_link as any)?.url,
        icon: network?.custom_social_link_icon,
      },
    };
  }, {});

  const hasSocials = networks && Object.keys(networks)?.length > 0;
  const hasContactEmail =
    !!info?.contactEmail && info.contactEmail?.trim() !== '';

  const minWidhtSocials = Object.keys(networks || {}).length * 52.5;

  const activeWorkingHours = getActiveWorkingHours({
    adminWorkingHours: adminWorkingHours as ScheduleWorkingHours[],
    configWorkingHours: configWorkingHours,
  });

  const sectionTitleColor = getCSSpropety(
    'color',
    styleProps?.sectionTitleColor,
  );
  const infoBackgroundColor = getCSSpropety(
    'background',
    styleProps?.infoBackgroundColor,
  );
  const infoLabelColor = getCSSpropety('color', styleProps?.infoLabelColor);
  const infoTextColor = getCSSpropety('color', styleProps?.infoTextColor);

  const mapStyleSelected = getMapStyle(info?.mapStyle);

  return (
    <section className={style.location__info}>
      <Container
        className={classNames(style.location__info_grid_container, {
          [style.location__info_grid_container_with_description]:
            info?.enableBlogStyle,
        })}
      >
        <div className={style.location__info_content}>
          {!!activeWorkingHours?.length && (
            <div className={style.location__info_body}>
              <Heading
                className={style.info__title}
                level={2}
                style={sectionTitleColor}
              >
                Hours
              </Heading>
              <ol className={style.location__info_list}>
                {activeWorkingHours?.map((schedule, id) => {
                  return (
                    <li
                      className={style.location__info_list_item}
                      key={id}
                      style={infoBackgroundColor}
                    >
                      <span
                        className={style.location__info_list_item_label}
                        style={infoLabelColor}
                      >
                        {startCase(schedule?.day_of_the_week?.toLowerCase())}
                      </span>
                      <span
                        className={style.location__info_list_item_value}
                        style={infoTextColor}
                      >
                        {!schedule?.start || !schedule?.end
                          ? 'Not Provider'
                          : `${transformMilitaryTime(
                              schedule.start,
                            )} - ${transformMilitaryTime(schedule.end)}`}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
          <div className={style.location__info_body}>
            <Heading
              className={style.info__title}
              level={2}
              style={sectionTitleColor}
            >
              Get in touch
            </Heading>
            <address className={style.location__info_list}>
              <span className={style.location__info_list_item_contact}>
                <span className={style.location__info_list_item_label}>
                  Phone Number
                </span>
                <Link
                  aria-label={`Call ${info.phone}`}
                  href={`tel:${info.phone}`}
                >
                  {formatEnUSPhoneNumber(info.phone)}
                </Link>
              </span>
              {hasContactEmail && (
                <span className={style.location__info_list_item_contact}>
                  <span className={style.location__info_list_item_label}>
                    Email
                  </span>
                  <Link
                    aria-label={`Send email to ${info.contactEmail}`}
                    href={`mailto:${info.contactEmail}`}
                  >
                    {info.contactEmail}
                  </Link>
                </span>
              )}
              <span className={style.location__info_list_item_contact}>
                <span className={style.location__info_list_item_label}>
                  Address
                </span>
                <Link
                  aria-label={`View the address ${info.googleMapUrl} in a new tab`}
                  href={info.googleMapUrl ?? '/'}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {info.fullAddress}
                </Link>
              </span>
              {hasSocials && (
                <span className={style.location__info_list_item_contact}>
                  <span className={style.location__info_list_item_label}>
                    Socials
                  </span>
                  <Socials
                    className={classNames(style.grid__element_info_socials)}
                    networks={networks}
                    style={{ minWidth: `${minWidhtSocials}px` }}
                  />
                </span>
              )}
            </address>
          </div>
        </div>
        <div className={style.location__info_map}>
          {asText(info.prismicDescription) && (
            <div
              className={classNames(
                style.location__info_description,
                'content',
                'blog-article__content',
              )}
            >
              <PrismicRichText
                components={htmlSerializerWithProps({
                  color: styleProps?.infoTextColor,
                })}
                field={info.prismicDescription}
              />
            </div>
          )}
          <Maps
            className={style.location__info_map_container}
            defaultMarker={info?.defaultMarker?.url!}
            hideInterestingPoints
            locations={[
              {
                label: info?.heading,
                lat: info.geoLoc.lat,
                lng: info.geoLoc.lng,
                title: info?.heading,
                photo: info?.storeImage,
              },
            ]}
            mapStyleTokens={mapStyleSelected}
            markerStyle={{
              titleColor: info?.markerTitleColor!,
              defaultMarkerColor: info?.defaultPinColor!,
            }}
            narrowMapTooltip
          />
        </div>
      </Container>
    </section>
  );
};

interface ILocationStyleProps {
  backgroundPage?: ColorField;
  sectionTitleColor?: ColorField;
  infoBackgroundColor?: ColorField;
  infoLabelColor?: ColorField;
  infoTextColor?: ColorField;
}

interface ILocationInfoProps {
  info: ILocationInfo;
  styleProps?: ILocationStyleProps;
  locationUid?: string;
}

export default LocationInfo;
